import { useCallback, useEffect, useState } from "react";
import {
  apiGet,
  apiGetAll,
  listify,
  getOtherTasks,
  getOtherTaskTypes,
} from "../../api";

// Data loader for the Tasks grid.
//
// Everything loads ONCE, on mount. Tab switching is pure client-side filtering
// and issues no network requests.
//
// This used to key three separate effects on activeTab, so every tab switch
// refetched, and the Other tab was loaded twice over (once here, once inside
// OtherTasksTable, which remounted on each visit). /api/tasks/ is no longer
// filtered by task_category either — one unfiltered call returns every
// category and the tabs slice it locally.
//
// The setters are returned as well as the values because the grid does
// optimistic updates: it patches the row locally, then refetches.
const normalizeEmployees = (emp) =>
  listify(emp).map((e) => ({
    ...e,
    name:
      e.full_name ||
      `${e.first_name || ""} ${e.last_name || ""}`.trim() ||
      e.username ||
      "Unknown",
  }));

// Ensure assigned_employees is always an array of {id, full_name}.
export const normalizeRows = (rows) =>
  listify(rows).map((row) => {
    if (!row.assigned_employees || !Array.isArray(row.assigned_employees)) {
      row.assigned_employees = [];
    }
    if (
      row.assigned_employees.length > 0 &&
      typeof row.assigned_employees[0] === "object"
    ) {
      row.assigned_employees = row.assigned_employees.map((e) => ({
        id: parseInt(e.id, 10),
        full_name: e.full_name,
      }));
    }
    return row;
  });

export default function useTasksData(horizon = "active") {
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState({
    general: [],
    tax_filing: [],
    priority: [],
  });
  const [otherTasks, setOtherTasks] = useState([]);
  const [otherTaskTypes, setOtherTaskTypes] = useState([]);
  const [otherLoading, setOtherLoading] = useState(true);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // The main grid: every task, every category. The tabs filter this locally.
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const [c, inv, emp, statusData] = await Promise.all([
        // horizon defaults to `active` server-side; the Tasks page passes it
        // explicitly so the intent is readable here rather than implied.
        apiGetAll(`/api/tasks/?horizon=${horizon}`),
        apiGetAll("/api/invoices/"),
        apiGet("/api/employees/"),
        apiGet("/api/status-options/by_type/"),
      ]);

      setClients(normalizeRows(c));
      setInvoices(listify(inv));
      setEmployees(normalizeEmployees(emp));
      setStatuses(statusData);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  // The Other tab. Owned here rather than inside OtherTasksTable so that
  // remounting the table on a tab switch does not refetch. Mutations still
  // call this to refresh.
  const reloadOtherTasks = useCallback(async () => {
    setOtherLoading(true);
    try {
      const [tasks, types] = await Promise.all([
        // Same horizon as the main grid. Roll-forward creates recurring
        // Other tasks months ahead, so without this the Other tab is the
        // one list the planning horizon never reached.
        getOtherTasks({ horizon }),
        getOtherTaskTypes(),
      ]);
      setOtherTasks(Array.isArray(tasks) ? tasks : []);
      setOtherTaskTypes(Array.isArray(types) ? types : []);
    } catch (e) {
      console.error("Failed to load other tasks", e);
    } finally {
      setOtherLoading(false);
    }
  }, [horizon]);

  // The full client roster, for the Other Tasks client picker.
  const fetchAllClients = useCallback(async () => {
    try {
      const data = await apiGetAll("/api/clients/");
      setAllClients(listify(data));
    } catch {
      setAllClients([]);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    reloadOtherTasks();
    fetchAllClients();
    // horizon is listed explicitly (not just via fetchClients) so a flip of
    // the "Include planning" chip always triggers a refetch from the server
    // — the server decides which rows are in the horizon, not the client.
  }, [fetchClients, reloadOtherTasks, fetchAllClients, horizon]);

  return {
    clients,
    setClients,
    allClients,
    invoices,
    employees,
    statuses,
    err,
    setErr,
    loading,
    refetch: fetchClients,

    otherTasks,
    setOtherTasks,
    otherTaskTypes,
    setOtherTaskTypes,
    otherLoading,
    reloadOtherTasks,
  };
}
